import { z } from "zod";
export const ModelTaskSchema = z.enum([
    "MESSAGE_AMBIGUITY",
    "NATURAL_WORK_DISCOVERY",
    "ANSWER_COMPRESSION",
    "MEDIA_INSPECTION",
    "OPS_COMMAND_INTERPRETATION",
    "OPS_AGENT_TURN",
    "OPS_WEB_RESEARCH",
    "OPS_PROGRESS_PRESENTATION"
]);
// v2 permits evidence-backed wording correction; v1 froze incorrect claims.
// v5 also receives the model-resolved current request without prior Chat. v6
// makes each proof correction one complete nonempty replacement instead of two
// independently valid deltas. Keep cache/receipt identity distinct so an old
// proof-only or delta output is not reused. v7 teaches the checker that an
// attached REVOKE annotation is status metadata on the existing target proof,
// never deleted body or actor attribution. v8 adds semantic leave-one-out
// minimization; v9/output-v6 adds only the model-declared request-irrelevant
// whole-item removal disposition.
export const OPS_PRIMARY_WHATSAPP_PROOF_CORRECTIONS_PROMPT_VERSION = "ops-primary-whatsapp-proof-corrections-v9";
export const OPS_PRIMARY_WHATSAPP_PROOF_CORRECTIONS_SCHEMA_VERSION = "ops-primary-whatsapp-proof-corrections-output-v6";
export const OPS_PRIMARY_WHATSAPP_INDEPENDENT_READING_PROMPT_VERSION = "ops-primary-whatsapp-independent-reading-v2";
export const OPS_PRIMARY_WHATSAPP_INDEPENDENT_READING_SCHEMA_VERSION = "ops-primary-whatsapp-independent-reading-output-v1";
// The optional independent-reading carrier shares the correction instruction
// and output schema. Version it with that wire change without enabling its
// separate third-call path.
export const OPS_PRIMARY_WHATSAPP_INDEPENDENT_REVIEW_PROMPT_VERSION = "ops-primary-whatsapp-independent-review-v4";
export const OPS_PRIMARY_WHATSAPP_INDEPENDENT_REVIEW_SCHEMA_VERSION = "ops-primary-whatsapp-independent-review-output-v2";
export const ModelProviderFallbackRoleSchema = z.enum([
    "START_AVAILABILITY_FALLBACK",
    "TERMINAL_START_FALLBACK"
]);
export const ModelProviderFallbackRouteSchema = z.object({
    role: ModelProviderFallbackRoleSchema,
    provider: z.string().min(1).max(80),
    model: z.string().min(1).max(160)
}).strict();
// WHY [BOL-PRIMARY-CHECKER-INPUT-001]: R145 replayed R135's exact saved draft
// and source wire. The checker never called Sol: its conservative UTF-8-byte
// estimate was 139,095 against our 128,000 cap. Installed Codex 0.153.4's Sol
// catalog has 272,000 context; its 90% auto-compaction threshold is 244,800.
// Reserve the existing 40,000 accepted output allowance, strictly below that
// threshold: 244,800 - 40,000 - 1. The byte estimate includes 7,000 runner
// overhead and is NOT an exact tokenizer. Output is checked after the native
// call, not limited natively, so this does not guarantee no compaction. Replay
// full-source root/checker admission when the runner catalog/accounting changes.
export const OPS_PRIMARY_SOL_INPUT_TOKEN_CAP = 204_799;
export const OpsAgentPrimaryFinalPolicySchema = z.object({
    provider: z.literal("openai"),
    model: z.literal("gpt-5.6-sol"),
    reasoningEffort: z.literal("max"),
    // Retain stored 128k policies during merge-forward rollout; absent values
    // retain their generic bound. This capacity never belongs to ordinary calls.
    maxInputTokens: z.union([z.literal(128_000), z.literal(OPS_PRIMARY_SOL_INPUT_TOKEN_CAP)]).optional(),
    maxOutputTokens: z.literal(40_000),
    // WHY [BOL-PRIMARY-COMPLETION-DEADLINE-001]: R45 received native Sol
    // response deltas but no completed result before the 600s API cancellation.
    // Exact last-progress timing was not retained. Thirty minutes permits a
    // bounded opportunity to finish, not a
    // latency or accuracy prediction. Accept 600 seconds only for stored policy
    // compatibility; migration 087 selects 30 minutes for agency-operations.
    // Generic model timeouts, provider identity, retry and fallback stay closed.
    timeoutMs: z.union([z.literal(600_000), z.literal(1_800_000)])
}).strict();
// Installed Codex metadata reports a 272k context window and applies its 95%
// effective-window guard. Keep the ordinary selected Sol turn's 5k output
// policy reserved inside that effective window: 258,400 - 5,000 = 253,400.
// This is route metadata, not a larger generic policy or a token-count claim.
export const SELECTED_SOL_OPS_AGENT_INPUT_TOKEN_CAP = 253_400;
export const ModelExecutionPolicySchema = z.object({
    enabled: z.boolean(),
    provider: z.string().min(1).max(80).nullable(),
    model: z.string().min(1).max(160).nullable(),
    opsAgentProvider: z.string().min(1).max(80).nullable().optional(),
    opsAgentModel: z.string().min(1).max(160).nullable().optional(),
    opsAgentInputTokenCap: z.literal(SELECTED_SOL_OPS_AGENT_INPUT_TOKEN_CAP).nullable().optional(),
    quickProvider: z.string().min(1).max(80).nullable().optional(),
    quickModel: z.string().min(1).max(160).nullable().default(null),
    fallbackRoutes: z.array(ModelProviderFallbackRouteSchema).max(3).default([]),
    taskModels: z.partialRecord(ModelTaskSchema, z.string().min(1).max(160)).optional(),
    /** Exact uncached J1 answer/proof owner after the quick clarity decision and
     * complete read. It is deliberately outside ordinary task/fallback routes. */
    opsAgentPrimaryFinal: OpsAgentPrimaryFinalPolicySchema.nullable().default(null),
    allowedTasks: z.array(ModelTaskSchema).max(8),
    dailyCallCap: z.int().min(0).max(10_000),
    dailyInputTokenCap: z.int().min(0).max(10_000_000),
    dailyOutputTokenCap: z.int().min(0).max(2_000_000),
    dailyCostMicrosCap: z.int().min(0).max(1_000_000_000_000),
    monthlyCallCap: z.int().min(0).max(100_000),
    monthlyInputTokenCap: z.int().min(0).max(100_000_000),
    monthlyOutputTokenCap: z.int().min(0).max(20_000_000),
    monthlyCostMicrosCap: z.int().min(0).max(9_000_000_000_000),
    perRunInputTokenCap: z.int().min(1).max(80_000),
    perRunOutputTokenCap: z.int().min(1).max(5_000),
    timeoutMs: z.int().min(10).max(180_000),
    minimumConfidence: z.int().min(0).max(100),
    reuseMaxAgeHours: z.int().min(1).max(720),
    version: z.int().positive()
}).superRefine((policy, context) => {
    if (policy.enabled) {
        if (!policy.provider || !policy.model) {
            context.addIssue({ code: "custom", message: "Enabled model policy requires provider and model." });
        }
        if (policy.allowedTasks.length === 0) {
            context.addIssue({ code: "custom", message: "Enabled model policy requires at least one task." });
        }
    }
    for (const task of Object.keys(policy.taskModels ?? {})) {
        if (!policy.allowedTasks.includes(task)) {
            context.addIssue({ code: "custom", path: ["taskModels", task], message: "A task model requires that task to be allowed." });
        }
    }
    if ((policy.opsAgentProvider == null) !== (policy.opsAgentModel == null)) {
        context.addIssue({
            code: "custom",
            path: ["opsAgentProvider"],
            message: "The selected Ops agent route requires both provider and model."
        });
    }
    const selectedSol = policy.opsAgentProvider === "openai"
        && policy.opsAgentModel === "gpt-5.6-sol";
    if (selectedSol !== (policy.opsAgentInputTokenCap === SELECTED_SOL_OPS_AGENT_INPUT_TOKEN_CAP)) {
        context.addIssue({
            code: "custom",
            path: ["opsAgentInputTokenCap"],
            message: "The selected Sol Ops route requires its exact provider-bound input allowance."
        });
    }
    if (policy.quickProvider != null && policy.quickModel === null) {
        context.addIssue({
            code: "custom",
            path: ["quickProvider"],
            message: "The quick intent route requires both provider and model."
        });
    }
    const opsAgentProvider = policy.opsAgentProvider ?? policy.provider;
    const opsAgentModel = policy.opsAgentModel ?? policy.model;
    const quickProvider = policy.quickProvider ?? policy.provider;
    if (policy.quickModel
        && quickProvider === opsAgentProvider
        && policy.quickModel === opsAgentModel) {
        context.addIssue({
            code: "custom",
            path: ["quickModel"],
            message: "The quick intent route must be distinct from the selected Ops agent route."
        });
    }
    const routeIdentities = new Set();
    let terminalIndex = -1;
    policy.fallbackRoutes.forEach((route, index) => {
        const identity = `${route.provider}\u0000${route.model}`;
        if (routeIdentities.has(identity)
            || (route.provider === policy.provider && route.model === policy.model)
            || (route.provider === quickProvider && route.model === policy.quickModel)
            || (route.provider === opsAgentProvider && route.model === opsAgentModel)) {
            context.addIssue({
                code: "custom",
                path: ["fallbackRoutes", index],
                message: "Every physical fallback route must have one distinct provider/model identity."
            });
        }
        routeIdentities.add(identity);
        if (route.role === "TERMINAL_START_FALLBACK") {
            if (terminalIndex !== -1 || index !== policy.fallbackRoutes.length - 1) {
                context.addIssue({
                    code: "custom",
                    path: ["fallbackRoutes", index, "role"],
                    message: "The terminal START fallback must be unique and last."
                });
            }
            terminalIndex = index;
        }
        else if (terminalIndex !== -1) {
            context.addIssue({
                code: "custom",
                path: ["fallbackRoutes", index, "role"],
                message: "A START-availability fallback cannot follow the terminal route."
            });
        }
    });
    if (Object.keys(policy.taskModels ?? {}).length > 0 && terminalIndex === -1) {
        context.addIssue({
            code: "custom",
            path: ["taskModels"],
            message: "Task-specific models require one terminal START fallback route."
        });
    }
    const directFinal = policy.opsAgentPrimaryFinal;
    if (directFinal && ((policy.provider === directFinal.provider
        && [policy.model, policy.quickModel].includes(directFinal.model))
        || policy.fallbackRoutes.some((route) => route.provider === directFinal.provider
            && (route.model === directFinal.model
                || (route.role === "TERMINAL_START_FALLBACK"
                    && policy.taskModels?.OPS_AGENT_TURN === directFinal.model))))) {
        context.addIssue({
            code: "custom",
            path: ["opsAgentPrimaryFinal"],
            message: "The direct J1 final must remain outside every ordinary and fallback route."
        });
    }
});
/**
 * WHY [BOL-SELECTED-OPS-INPUT-BUDGET-001]: the real selected-Sol native
 * continuation was conservatively counted as 134,412 and rejected by the
 * unrelated generic 80k fence before reservation. Only an exact configured
 * OPS_AGENT_TURN provider/model may use its atomically stored allowance.
 * Quick, autonomous, fallback, mismatched, and legacy routes retain the
 * generic bound; the ledger still reserves and settles full usage.
 * Falsifier: another identity borrows this allowance, or selected Sol is
 * blocked at 80k despite the matching versioned policy tuple.
 */
export function modelInputTokenCapForTask(policy, task, provider, model) {
    if (task === "OPS_AGENT_TURN"
        && policy.opsAgentProvider === provider
        && policy.opsAgentModel === model
        && policy.opsAgentInputTokenCap !== null
        && policy.opsAgentInputTokenCap !== undefined) {
        return policy.opsAgentInputTokenCap;
    }
    return policy.perRunInputTokenCap;
}
/**
 * WHY [BOL-SELECTED-OPS-OUTPUT-BUDGET-001]: native Sol CONTINUE5957fd48
 * completed with 9,813 total output tokens, then the unrelated 5k generic
 * budget discarded it before final validation. Selected deep work uses the
 * already configured matching model's total-output allowance (including
 * reasoning), not a new global limit or a visible-answer-length limit.
 * Preparation, durable reservation and settlement must use this same owner.
 * Quick/autonomous/other models and legacy policies keep their generic cap.
 * Falsifier: the matching 40k policy rejects that 9,813 result, another route
 * borrows the allowance, or usage above the configured allowance is accepted.
 */
export function modelOutputTokenCapForTask(policy, task, provider, model) {
    const deep = policy.opsAgentPrimaryFinal;
    if (task === "OPS_AGENT_TURN"
        && policy.opsAgentProvider === provider
        && policy.opsAgentModel === model
        && deep?.provider === provider
        && deep.model === model) {
        return deep.maxOutputTokens;
    }
    return policy.perRunOutputTokenCap;
}
/**
 * WHY [BOL-SELECTED-OPS-DEADLINE-001]: selected Sol CONTINUE
 * 111db8fe-cda4-4270-a8ce-b88de7e2c582 reserved the generic 120-second
 * deadline, expired in the ledger, and admitted another run while its matching
 * 1,800-second physical owner was still active. Resolve the deadline from the
 * same versioned provider/model tuple that owns the prepared call; unrelated,
 * legacy, quick, and fallback routes retain the generic deadline. This changes
 * neither cancellation nor retry/fallback authority. Falsifier: a matching
 * selected call expires before its declared physical deadline, another route
 * borrows that deadline, or Stop no longer terminates the call.
 */
export function modelTimeoutMsForTask(policy, task, provider, model) {
    const physical = policy.opsAgentPrimaryFinal;
    if (task === "OPS_AGENT_TURN"
        && policy.opsAgentProvider === provider
        && policy.opsAgentModel === model
        && physical?.provider === provider
        && physical.model === model) {
        return physical.timeoutMs;
    }
    return policy.timeoutMs;
}
/**
 * WHY [BOL-TRUTHFUL-PROVIDER-IDENTITY-001]: `provider`/`model` are the
 * physical autonomous primary, never a virtual selector alias. Legacy
 * `taskModels` still overrides only the configured terminal START route;
 * `opsAgentProvider`/`opsAgentModel` is the separate explicit no-fallback
 * selector for OPS_AGENT_TURN. Falsifier: selection relabels a physical call,
 * changes autonomous routing, or reaches a fallback after selected START.
 */
export function modelForTask(policy, task) {
    if (["MESSAGE_AMBIGUITY", "OPS_PROGRESS_PRESENTATION"].includes(task)
        && policy.quickModel)
        return policy.quickModel;
    if (task === "OPS_AGENT_TURN" && policy.opsAgentModel)
        return policy.opsAgentModel;
    return policy.model;
}
export function modelRoutesForTask(policy, task) {
    if (!policy.provider || !policy.model)
        return [];
    const deepRoutes = [
        { role: "PRIMARY", provider: policy.provider, model: policy.model },
        ...policy.fallbackRoutes.map((route) => ({
            ...route,
            model: route.role === "TERMINAL_START_FALLBACK"
                ? policy.taskModels?.[task] ?? route.model
                : route.model
        }))
    ];
    // WHY [BOL-OPS-QUICK-DEEP-POLICY-001]: the live J1 ran the cheap
    // Flash-Lite clarity classifier as the complete-window reasoning owner and
    // omitted material facts/proof. MESSAGE_AMBIGUITY is the only task allowed
    // to start the separately declared quick model; a CLEAR decision releases
    // it before the ordinary OPS_AGENT_TURN reserves the deep primary. The quick
    // decision has no fallback into semantic work: provider/model unavailability
    // fails that optional classifier closed and cannot silently make a deep route
    // answer a different task. Falsifier: a semantic/tool turn reserves
    // quickModel, MESSAGE_AMBIGUITY reaches any deep/fallback route, a quick
    // CLEAR binds deep affinity, or presentation output gains answer authority.
    // OPS_PROGRESS_PRESENTATION is the second and only other quick task: it
    // paraphrases one private high-level progress observation without tools,
    // facts, effects, fallback, or final-answer authority.
    if (["MESSAGE_AMBIGUITY", "OPS_PROGRESS_PRESENTATION"].includes(task)
        && policy.quickModel) {
        return [{ role: "PRIMARY", provider: policy.quickProvider ?? policy.provider, model: policy.quickModel }];
    }
    // WHY [BOL-OPS-SELECTED-DEEP-TASK-001]: the manual model setting owns every
    // user-request OPS_AGENT_TURN in API and Worker, while proactive
    // NATURAL_WORK_DISCOVERY retains its separate autonomous route. A selected
    // turn has one physical owner and never inherits legacy START fallback.
    // Falsifier: an OPS_AGENT_TURN reaches provider/model or fallbackRoutes after
    // an explicit Ops route exists, or NATURAL_WORK_DISCOVERY changes with it.
    if (task === "OPS_AGENT_TURN" && policy.opsAgentProvider && policy.opsAgentModel) {
        return [{ role: "PRIMARY", provider: policy.opsAgentProvider, model: policy.opsAgentModel }];
    }
    return deepRoutes;
}
export function modelRouteForProvider(policy, task, provider) {
    return modelRoutesForTask(policy, task).find((route) => route.provider === provider) ?? null;
}
export const AgentBrainRouteSchema = z.object({
    role: z.enum(["PRIMARY", "START_AVAILABILITY_FALLBACK", "TERMINAL_START_FALLBACK"]),
    provider: z.enum(["google", "anthropic", "openai"]),
    model: z.string().min(1).max(160),
    label: z.string().min(1).max(80),
    note: z.string().min(1).max(160)
}).strict();
export const AgentBrainSettingsSchema = z.object({
    agentActive: z.boolean(),
    executionMode: z.enum(["MANUAL", "AUTO"]),
    primary: AgentBrainRouteSchema.extend({ role: z.literal("PRIMARY") }).strict(),
    startAvailabilityFallbacks: z.array(AgentBrainRouteSchema.extend({ role: z.literal("START_AVAILABILITY_FALLBACK") }).strict()).max(2),
    terminalStartFallback: AgentBrainRouteSchema.extend({
        role: z.literal("TERMINAL_START_FALLBACK")
    }).strict().nullable(),
    selectablePrimaries: z.array(AgentBrainRouteSchema.extend({ role: z.literal("PRIMARY") }).strict()).min(1).max(2),
    modelCallsEnabled: z.boolean(),
    version: z.int().positive()
}).strict();
export const UpdateAgentBrainSettingsSchema = z.discriminatedUnion("provider", [
    z.object({
        provider: z.literal("google"),
        model: z.literal("gemini-3.7-flash"),
        expectedVersion: z.int().positive()
    }).strict(),
    z.object({
        provider: z.literal("openai"),
        model: z.literal("gpt-5.6-sol"),
        expectedVersion: z.int().positive()
    }).strict()
]);
//# sourceMappingURL=model-boundary.js.map